import { Check, ChevronDown, MessageChatCircle } from "@untitledui/icons";
import { useState } from "react";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import type { ModalRiskRowItem, SafetyPolicyOption } from "./ModalRiskRow";
import { ModalRiskRow } from "./ModalRiskRow";
import type { RiskConfigFocus } from "./RiskConfigModal";
import { CORE_SAFETY_LOCKED_TOOLTIP, GROUP_ICONS, matchesRisk, type RiskGroup, type RiskItem } from "./risk-groups";

type ModalGroupSectionProps = {
  group: RiskGroup;
  policies: SafetyPolicyOption[];
  domainKnowledge: Record<string, string>;
  getRiskState: (item: RiskItem) => ModalRiskRowItem;
  onRiskChange: (item: RiskItem, patch: Partial<ModalRiskRowItem>) => void;
  onDomainKnowledgeChange: (key: string, values: string[]) => void;
  managedPlaceholdersEditable?: boolean;
  focus?: RiskConfigFocus;
};

export const ModalGroupSection = ({
  group,
  policies,
  domainKnowledge,
  getRiskState,
  onRiskChange,
  onDomainKnowledgeChange,
  managedPlaceholdersEditable,
  focus,
}: ModalGroupSectionProps) => {
  const isFocused = (item: RiskItem) => !!focus && matchesRisk(item, focus.riskId);
  const [isOpen, setIsOpen] = useState(() => group.items.some(isFocused));

  const isLocked = group.id === "baseline";
  const risks = group.items.map((item) => getRiskState(item));
  const active = risks.filter((r) => r.enabled).length;
  const total = risks.length;
  const allActive = active === total;
  const Icon = GROUP_ICONS[group.id] ?? MessageChatCircle;

  const toggleAll = (val: boolean) => {
    for (const item of group.items) {
      onRiskChange(item, { enabled: val });
    }
  };

  return (
    <div className="rounded-xl border border-border-primary bg-white overflow-hidden">
      {/* A <button> can't contain the nested Toggle/Tooltip (interactive-in-interactive), so this
          mimics button semantics on a div instead. */}
      {/* biome-ignore lint/a11y/useSemanticElements: can't be a real <button> — it contains the nested Toggle/Tooltip */}
      <div
        role="button"
        tabIndex={0}
        className="w-full flex items-center gap-3 px-5 py-4 text-left cursor-pointer hover:bg-secondary transition-colors"
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen((v) => !v);
          }
        }}
      >
        <span className="flex-1 flex items-center gap-3 min-w-0">
          <Icon aria-hidden="true" className="w-[18px] h-[18px] text-secondary shrink-0" />
          <span className="text-sm font-medium text-primary truncate">{group.name}</span>
        </span>

        <span
          className={cx(
            "text-xs font-medium flex items-center gap-1.5",
            active > 0 ? "text-success-700" : "text-tertiary",
          )}
        >
          {active} of {total} Risks Active
          {active > 0 && <Check aria-hidden="true" className="w-3.5 h-3.5 text-success-600" />}
        </span>

        {/* biome-ignore lint/a11y/useKeyWithClickEvents: only stops the nested Toggle's click from bubbling into the collapse handler above, not itself interactive */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: only stops the nested Toggle's click from bubbling into the collapse handler above, not itself interactive */}
        <div onClick={(e) => e.stopPropagation()}>
          {isLocked ? (
            <Tooltip title={CORE_SAFETY_LOCKED_TOOLTIP}>
              <Toggle isSelected={allActive} isDisabled onChange={toggleAll} size="sm" />
            </Tooltip>
          ) : (
            <Toggle isSelected={allActive} onChange={toggleAll} size="sm" />
          )}
        </div>

        <span className="w-7 h-7 flex items-center justify-center rounded">
          <ChevronDown
            aria-hidden="true"
            className={cx("w-4 h-4 text-quaternary transition-transform duration-150", isOpen && "rotate-180")}
          />
        </span>
      </div>

      {isOpen && (
        <div>
          {group.items.map((item) => {
            // The "request video metadata edit" policy only applies to required_text
            // risks (its sequence branches on whether the caption was edited), so hide
            // it as an option for any other model type.
            const rowPolicies =
              item.modelType === "required_text"
                ? policies
                : policies.filter((p) => p.type !== "request_metadata_edit");
            return (
              <ModalRiskRow
                key={item.key}
                item={getRiskState(item)}
                policies={rowPolicies}
                domainKnowledge={domainKnowledge}
                onChange={(patch) => onRiskChange(item, patch as Partial<ModalRiskRowItem>)}
                onDomainKnowledgeChange={onDomainKnowledgeChange}
                managedPlaceholdersEditable={managedPlaceholdersEditable}
                focusAction={isFocused(item) ? focus?.action : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
